/**
 * Keep the coach and the bay when a booking is moved (DNKADM-0488) — shared by Edit time
 * (`EditReservationDetailsDialog`) and Reschedule (`RescheduleDialog`).
 *
 * **Pinned by default.** The booking's current coach and bay pin the availability read
 * (`staffId` / `resourceId`, the DNKADM-0443 / DNKADM-0484 pins), so with `hideUnavailable`
 * the picker offers only the times that coach AND that bay are free, and the write sends both
 * ids explicitly, so dink-api never re-picks them (DNKAPI-0548: an explicit id is honoured under
 * `ROUND_ROBIN` and `MEMBER_SELECTS`).
 *
 * **Change coach / Change bay.** Each reveals a list of the service's eligible coaches or bays at
 * the booking's location (the Book on behalf pools), loaded only when asked for. A different
 * coach or bay re-pins the read to it. The bay list also offers "Any free bay": the read is
 * unpinned, and once a time is picked every bay is checked AT THAT TIME (one pinned read per
 * bay, for that day — the server stays the availability authority). The current bay is kept
 * when it is free; when it is not, staff see "Bay N is taken at this time" and must pick one
 * of the free bays. The bay is never switched silently.
 *
 * A dimension the booking has no current assignment for, or whose service mode is `NONE`, is
 * inactive: nothing is pinned or sent for it, exactly as before this unit.
 *
 * The hook lives here; its UI (`KeptAssignmentFields`, `BayCheckNotice`) is in `KeptAssignmentFields.tsx`.
 */
import { useRef, useState } from 'react';
import { getMemberAvailability, type AvailabilitySlot } from '@/api/availability';
import type { Reservation } from '@/api/reservations';
import { getServiceResources, getServiceStaff } from '@/api/services';
import { listResources } from '@/api/resources';
import { listStaff, staffDisplayName } from '@/api/staff';
import type { AssignmentMode } from '@/domain/v2Enums';
import { useResource, type ResourceStatus } from '@/hooks/useResource';

/** The bay select's "Any free bay" value: the read is unpinned and the bay is checked per time. */
export const ANY_BAY = '';

/** A coach or bay the booking can be kept on or moved to. */
export interface KeptOption {
  id: string;
  name: string | null;
}

/** The per-time bay check for "Any free bay" (null = no time picked from the picker). */
interface BayCheck {
  start: string;
  status: 'loading' | 'ready' | 'error';
  free: KeptOption[];
}

interface Dimension {
  /** The booking has a current assignment here and the service mode is not `NONE`. */
  active: boolean;
  current: KeptOption | null;
  /** The pinned id (the current one until staff change it); `ANY_BAY` only for the bay. */
  choice: string;
  setChoice: (id: string) => void;
  /** The Change list is showing (and its pool is loaded/loading). */
  changing: boolean;
  startChange: () => void;
  /** The current assignment first, then the eligible pool. */
  options: KeptOption[];
  poolStatus: ResourceStatus;
}

export interface KeepAssignment {
  coach: Dimension;
  bay: Dimension & {
    check: BayCheck | null;
    /** The free bay staff picked after "Bay N is taken at this time" ('' = none yet). */
    takenPick: string;
    setTakenPick: (id: string) => void;
    /** The bay the write sends, or null while it is unresolved (Save stays off). */
    resolved: string | null;
  };
  /** The availability pins + exclusion for {@link AvailabilitySlotPicker}. */
  pickerProps: {
    staffId?: string;
    resourceId?: string;
    excludeReservationUuid?: string;
    alwaysOfferStart?: string | null;
  };
  /** Tell the hook a time was picked from the picker (null = a manual time, or cleared). */
  onSlot: (slot: AvailabilitySlot | null) => void;
  /** The kept ids for the write body; a dimension that is inactive is absent. */
  writeIds: { staffId?: string; resourceId?: string };
}

interface UseKeepAssignmentArgs {
  reservation: Reservation;
  staffMode: AssignmentMode | null;
  resourceMode: AssignmentMode | null;
  /** A pin changed, so the host's chosen time no longer belongs to the read: clear it. */
  onPinsChange: () => void;
}

function mergeOptions(current: KeptOption | null, options: KeptOption[]): KeptOption[] {
  if (current === null || options.some((o) => o.id === current.id)) return options;
  return [current, ...options];
}

/** The day of an ISO-with-offset start, as written (the location's calendar day). */
function dayOf(iso: string): string | null {
  return /^\d{4}-\d{2}-\d{2}/.exec(iso)?.[0] ?? null;
}

/** The service's eligible, active coaches who work at this location (Book on behalf's pool). */
async function loadCoachPool(serviceId: string, locationId: string): Promise<KeptOption[]> {
  const [eligible, all] = await Promise.all([getServiceStaff(serviceId), listStaff()]);
  return all
    .filter(
      (m) =>
        m.isActive &&
        eligible.includes(m.id) &&
        (m.locations === undefined || m.locations.some((l) => l.id === locationId)),
    )
    .map((m) => ({ id: m.id, name: staffDisplayName(m) }));
}

/** The service's eligible, active bays at this location (Book on behalf's pool, DNKADM-0484). */
async function loadBayPool(serviceId: string, locationId: string): Promise<KeptOption[]> {
  const [eligible, all] = await Promise.all([
    getServiceResources(serviceId),
    listResources({ status: 'active' }),
  ]);
  return all
    .filter(
      (r) =>
        r.isActive &&
        eligible.includes(r.id) &&
        (r.locations === undefined || r.locations.some((l) => l.id === locationId)),
    )
    .map((r) => ({ id: r.id, name: r.name }));
}

export function useKeepAssignment({
  reservation,
  staffMode,
  resourceMode,
  onPinsChange,
}: UseKeepAssignmentArgs): KeepAssignment {
  const { serviceId, variationId, locationId, contactId, reservationUuid } = reservation;
  const currentCoach: KeptOption | null = reservation.assignedStaffId
    ? { id: reservation.assignedStaffId, name: reservation.assignedStaffName }
    : null;
  const currentBay: KeptOption | null = reservation.assignedResourceId
    ? { id: reservation.assignedResourceId, name: reservation.assignedResourceName }
    : null;
  const coachActive = currentCoach !== null && staffMode !== 'NONE';
  const bayActive = currentBay !== null && resourceMode !== 'NONE';

  const [coachChoice, setCoachChoiceState] = useState(currentCoach?.id ?? '');
  const [bayChoice, setBayChoiceState] = useState(currentBay?.id ?? ANY_BAY);
  const [changingCoach, setChangingCoach] = useState(false);
  const [changingBay, setChangingBay] = useState(false);
  const [check, setCheck] = useState<BayCheck | null>(null);
  const [takenPick, setTakenPick] = useState('');
  const checkRun = useRef(0);

  const coachPool = useResource(
    () =>
      changingCoach && coachActive && serviceId && locationId
        ? loadCoachPool(serviceId, locationId)
        : Promise.resolve<KeptOption[]>([]),
    [changingCoach, coachActive, serviceId, locationId],
  );
  const bayPool = useResource(
    () =>
      changingBay && bayActive && serviceId && locationId
        ? loadBayPool(serviceId, locationId)
        : Promise.resolve<KeptOption[]>([]),
    [changingBay, bayActive, serviceId, locationId],
  );
  const coachOptions = mergeOptions(currentCoach, coachPool.data ?? []);
  const bayOptions = mergeOptions(currentBay, bayPool.data ?? []);

  function clearCheck() {
    checkRun.current += 1;
    setCheck(null);
    setTakenPick('');
  }

  function setCoachChoice(id: string) {
    setCoachChoiceState(id);
    clearCheck();
    onPinsChange();
  }

  function setBayChoice(id: string) {
    setBayChoiceState(id);
    clearCheck();
    onPinsChange();
  }

  function onSlot(slot: AvailabilitySlot | null) {
    clearCheck();
    if (!bayActive || bayChoice !== ANY_BAY || slot === null || currentBay === null) return;
    const run = checkRun.current;
    const start = slot.start;
    const day = dayOf(start);
    // Without the loaded pool only the current bay could be checked, and "no other bay is free"
    // would be a claim about bays nobody asked about (QA 0488 touch 1).
    if (!day || !contactId || !serviceId || !variationId || !locationId || bayPool.status !== 'ready') {
      setCheck({ start, status: 'error', free: [] });
      return;
    }
    const wanted = Date.parse(start);
    // The booking's own start keeps its own bay (belt and braces for an API without
    // DNKAPI-0709, where the booking would read as blocking itself).
    const ownStart = reservation.start !== null && Date.parse(reservation.start) === wanted;
    setCheck({ start, status: 'loading', free: [] });
    Promise.all(
      bayOptions.map((bay) =>
        ownStart && bay.id === currentBay.id
          ? Promise.resolve<KeptOption | null>(bay)
          : getMemberAvailability(contactId, {
              serviceId,
              variationId,
              locationId,
              startDate: day,
              endDate: day,
              resourceId: bay.id,
              // The same coach + bay pair the write will send.
              ...(coachActive && coachChoice !== '' ? { staffId: coachChoice } : {}),
              ...(reservationUuid ? { excludeReservationUuid: reservationUuid } : {}),
            }).then((a) =>
              a.days.some((d) => d.slots.some((s) => s.available && Date.parse(s.start) === wanted))
                ? bay
                : null,
            ),
      ),
    ).then(
      (results) => {
        if (run !== checkRun.current) return;
        setCheck({ start, status: 'ready', free: results.filter((b): b is KeptOption => b !== null) });
      },
      () => {
        if (run !== checkRun.current) return;
        setCheck({ start, status: 'error', free: [] });
      },
    );
  }

  // The bay the write sends: a pinned bay as is; under "Any free bay" the current bay unless the
  // check found it taken — then only a bay staff picked from the free ones. A manual time (no
  // check) keeps the current bay; the server refuses it if busy, never re-picks it.
  let resolvedBay: string | null = null;
  if (bayActive && currentBay !== null) {
    if (bayChoice !== ANY_BAY) resolvedBay = bayChoice;
    else if (check === null) resolvedBay = currentBay.id;
    else if (check.status === 'ready') {
      if (check.free.some((b) => b.id === currentBay.id)) resolvedBay = currentBay.id;
      else if (takenPick !== '' && check.free.some((b) => b.id === takenPick)) resolvedBay = takenPick;
    }
  }

  // The own slot stays offered only while the picker is pinned to the booking's own coach and
  // bay (on another coach or bay it may genuinely be taken).
  const onOwnAssignment =
    (!coachActive || coachChoice === currentCoach?.id) && (!bayActive || bayChoice === currentBay?.id);

  return {
    coach: {
      active: coachActive,
      current: currentCoach,
      choice: coachChoice,
      setChoice: setCoachChoice,
      changing: changingCoach,
      startChange: () => setChangingCoach(true),
      options: coachOptions,
      poolStatus: coachPool.status,
    },
    bay: {
      active: bayActive,
      current: currentBay,
      choice: bayChoice,
      setChoice: setBayChoice,
      changing: changingBay,
      startChange: () => setChangingBay(true),
      options: bayOptions,
      poolStatus: bayPool.status,
      check,
      takenPick,
      setTakenPick,
      resolved: resolvedBay,
    },
    pickerProps: {
      ...(coachActive && coachChoice !== '' ? { staffId: coachChoice } : {}),
      ...(bayActive && bayChoice !== ANY_BAY ? { resourceId: bayChoice } : {}),
      ...(reservationUuid ? { excludeReservationUuid: reservationUuid } : {}),
      alwaysOfferStart: onOwnAssignment ? reservation.start : null,
    },
    onSlot,
    writeIds: {
      ...(coachActive && coachChoice !== '' ? { staffId: coachChoice } : {}),
      ...(resolvedBay !== null ? { resourceId: resolvedBay } : {}),
    },
  };
}
